import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GroupIcon from "@mui/icons-material/Group";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InsightsIcon from "@mui/icons-material/Insights";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { ResourceProps } from "react-admin";
import {
    JobApplicationList,
    JobApplicationEdit,
    JobApplicationCalendar,
} from "./job-applications";
import { DashboardsTabs } from "./job-applications/dashboards";
import { UserCreate, UserEdit, UserList } from "./users";
import { GroupCreate, GroupEdit, GroupList } from "./groups";
import { CourseManagementEdit, CourseManagementList } from "./courses-management";
import { MyCoursesList } from "./my-courses";
import { CompanyCreate, CompanyEdit, CompanyList, CompanyIcon } from "./company";
import { JobApplicationSourceCreate, JobApplicationSourceEdit, JobApplicationSourceList, JobApplicationSourceIcon } from "./job-application-source";
import { JobApplicationClosureReasonCreate, JobApplicationClosureReasonEdit, JobApplicationClosureReasonList, JobApplicationClosureReasonIcon } from "./job-application-closure-reasons";
import { JobApplicationStatusCreate, JobApplicationStatusEdit, JobApplicationStatusList, JobApplicationStatusIcon, WorkflowBuilder } from "./job-application-status";
import { RoleCreate, RoleEdit, RoleList, RoleShow, RoleIcon } from "./role";
import { MarketAnalysisList } from "./market-analysis";
import { SalesList } from "./sales";
import { GeographicAnalysisList } from "./geographic-analysis";
import { PipelineAnalysisList } from "./pipeline-analysis";
import { IrtList } from "./irt";

// Chat/Ticket resources
import { ContactList, ContactShow, ContactEdit, ContactCreate, ContactIcon } from "./contact";
import { ChannelList, ChannelShow, ChannelEdit, ChannelCreate, ChannelIcon } from "./channel";
import { TicketsPage, TicketsIcon, ConversationsPage, ConversationsIcon } from "./tickets";
import { TicketQueueList, TicketQueueCreate, TicketQueueEdit, TicketQueueIcon } from "./ticket-queue";
import { QuickReplyList, QuickReplyCreate, QuickReplyEdit, QuickReplyIcon } from "./quick-reply";
import { TagIcon } from "./tag";
import { TicketTagList, TicketTagCreate, TicketTagEdit, TicketTagIcon } from "./ticket-tag";
import { ConversationTagList, ConversationTagCreate, ConversationTagEdit, ConversationTagIcon } from "./conversation-tag";
import { JobApplicationTagList, JobApplicationTagCreate, JobApplicationTagEdit, JobApplicationTagIcon } from "./job-application-tag";

export const resources: ResourceProps[] = [
  {
    name: "user",
    list: UserList,
    create: UserCreate,
    edit: UserEdit,
    icon: PeopleIcon,
    recordRepresentation: (record) => {
      const name = record?.name || "";
      const email = record?.email || "";
      if (!name) {
        return email;
      }
      if (!email) {
        return name
      }
      return `${name} <${email}>`;
    },
  },
  {
    name: "group",
    list: GroupList,
    create: GroupCreate,
    edit: GroupEdit,
    icon: GroupIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    name: "job-application",
    list: JobApplicationList,
    icon: AssignmentIcon,
    recordRepresentation: (record) => record?.candidateName,
  },
  {
    name: "job-application-calendar",
    list: JobApplicationCalendar,
    icon: CalendarMonthIcon,
  },
  {
    name: "job-application-dashboard",
    list: DashboardsTabs,
    icon: InsightsIcon,
  },
  {
    name: "courses-management",
    list: CourseManagementList,
    edit: CourseManagementEdit,
    icon: SchoolIcon,
    recordRepresentation: (record) => record?.title,
  },
  {
    name: "my-courses",
    list: MyCoursesList,
    icon: SchoolIcon,
    recordRepresentation: (record) => record?.title,
  },
  {
    name: "company",
    list: CompanyList,
    create: CompanyCreate,
    edit: CompanyEdit,
    icon: CompanyIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    name: "job-application-source",
    list: JobApplicationSourceList,
    create: JobApplicationSourceCreate,
    edit: JobApplicationSourceEdit,
    icon: JobApplicationSourceIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    name: "job-application-closure-reason",
    list: JobApplicationClosureReasonList,
    create: JobApplicationClosureReasonCreate,
    edit: JobApplicationClosureReasonEdit,
    icon: JobApplicationClosureReasonIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    name: "job-application-status",
    list: JobApplicationStatusList,
    create: JobApplicationStatusCreate,
    edit: JobApplicationStatusEdit,
    icon: JobApplicationStatusIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    name: "job-application-status-workflow",
    list: WorkflowBuilder,
    icon: AccountTreeIcon,
  },
  {
    name: "role",
    list: RoleList,
    create: RoleCreate,
    edit: RoleEdit,
    show: RoleShow,
    icon: RoleIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    // Permission resource - used for fetching permissions in role form
    // No list/create/edit UI - permissions are system-defined
    name: "permission",
    recordRepresentation: (record) => record?.code,
  },
  {
    name: "business-intelligence/market-analysis",
    list: MarketAnalysisList,
    icon: BarChartIcon,
  },
  {
    name: "business-intelligence/sales",
    list: SalesList,
    icon: TrendingUpIcon,
  },
  {
    name: "business-intelligence/geographic-analysis",
    list: GeographicAnalysisList,
    icon: InsightsIcon,
  },
  {
    name: "business-intelligence/pipeline-analysis",
    list: PipelineAnalysisList,
    icon: InsightsIcon,
  },
  {
    name: "business-intelligence/irt",
    list: IrtList,
    icon: InsightsIcon,
  },

  // ============================================
  // CHAT / TICKET / CONVERSATION RESOURCES
  // ============================================
  {
    name: "tickets",
    list: TicketsPage,
    icon: TicketsIcon,
  },
  {
    name: "conversations",
    list: ConversationsPage,
    icon: ConversationsIcon,
  },
  {
    // Conversation resource - used internally for data fetching
    name: "conversation",
    recordRepresentation: (record) => record?.name || record?.contact?.name || `#${record?.id}`,
  },
  {
    // Ticket resource - used internally for data fetching
    name: "ticket",
    recordRepresentation: (record) => `#${record?.id}`,
  },
  {
    name: "contact",
    list: ContactList,
    show: ContactShow,
    edit: ContactEdit,
    create: ContactCreate,
    icon: ContactIcon,
    recordRepresentation: (record) => record?.name || record?.email || `#${record?.id}`,
  },
  {
    name: "channel",
    list: ChannelList,
    show: ChannelShow,
    edit: ChannelEdit,
    create: ChannelCreate,
    icon: ChannelIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    // Message resource - used internally, no list UI
    name: "message",
    recordRepresentation: (record) => record?.body?.substring(0, 50) || `#${record?.id}`,
  },
  {
    name: "ticket-queue",
    list: TicketQueueList,
    create: TicketQueueCreate,
    edit: TicketQueueEdit,
    icon: TicketQueueIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    name: "ticket-quick-reply",
    list: QuickReplyList,
    create: QuickReplyCreate,
    edit: QuickReplyEdit,
    icon: QuickReplyIcon,
    recordRepresentation: (record) => record?.title,
  },
  {
    // Internal tag resource - used by TicketTagSelector and other components
    name: "tag",
    icon: TagIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    name: "ticket-tag",
    list: TicketTagList,
    create: TicketTagCreate,
    edit: TicketTagEdit,
    icon: TicketTagIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    name: "conversation-tag",
    list: ConversationTagList,
    create: ConversationTagCreate,
    edit: ConversationTagEdit,
    icon: ConversationTagIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    name: "job-application-tag",
    list: JobApplicationTagList,
    create: JobApplicationTagCreate,
    edit: JobApplicationTagEdit,
    icon: JobApplicationTagIcon,
    recordRepresentation: (record) => record?.name,
  },
  {
    // Channel Connection resource - used for reference inputs
    name: "channel-connection",
    recordRepresentation: (record) => record?.displayName || record?.identifier,
  },
  {
    // Contact Identity resource - used internally
    name: "contact-identity",
    recordRepresentation: (record) => record?.name || record?.externalId,
  },
  {
    // Conversation Participant resource - used internally
    name: "conversation-participant",
    recordRepresentation: (record) => record?.user?.name || record?.contactIdentity?.name,
  },
];
